import type {Socket} from '@github/stable-socket'
import {StableSocket} from '@github/stable-socket'
import {fetchJson} from './fetch'

interface AuthenticatedURLResponse {
  data: {
    authenticated_url: string
  }
}

interface WsUrlLookupData {
  logStreamWebSocketUrl: string
}

export const MessageType = {
  Invocation: 1,
  StreamItem: 2,
  Completion: 3,
  StreamInvocation: 4,
  CancelInvocation: 5,
  Ping: 6,
  Close: 7,
} as const

export type MessageType = (typeof MessageType)[keyof typeof MessageType]

// https://github.com/dotnet/aspnetcore/blob/main/src/SignalR/docs/specs/HubProtocol.md#overview
type Message = HandshakeRequest | Invocation

interface HandshakeRequest {
  protocol: string
  version: number
}

interface Invocation {
  type: number
  target: string
  arguments: unknown[]
}

export interface SocketData<T> {
  target: string
  type: number
  arguments: T
}

export class SignalRSocket extends EventTarget {
  static readonly RECORD_SEPARATOR = String.fromCharCode(0x1e)
  // https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent
  static readonly ABNORMAL_CLOSURE = 1006
  static readonly POLICY_VIOLATION = 1008
  static readonly JITTER_MIN_MILLISECONDS = 100
  static readonly JITTER_MAX_MILLISECONDS = 400
  static #instances = new Map<string, SignalRSocket>()

  declare private globalSocket: Socket | null
  declare private socketUrl: string
  private isStreaming = false
  declare private xhrConnectionUrl: string
  private shouldTryAgainWithNewSocket = false

  static getInstance(target: string): SignalRSocket {
    if (!SignalRSocket.#instances.has(target)) {
      SignalRSocket.#instances.set(target, new SignalRSocket(target))
    }
    return SignalRSocket.#instances.get(target)!
  }

  private target: string
  constructor(target: string) {
    super()
    this.target = target
  }

  socketDidReceiveMessage(socket: Socket, message: string): void {
    const record = message.split(SignalRSocket.RECORD_SEPARATOR)[0]!
    const data = JSON.parse(record) as SocketData<unknown>

    this.dispatchEvent(new CustomEvent('socketDidReceiveMessage', {detail: data}))

    if (data.type === MessageType.Close) socket.close()
  }

  // Public methods
  socketDidOpen() {
    this.handshake()
  }

  socketDidClose() {
    // Do nothing
  }

  socketDidFinish() {
    this.globalSocket = null

    if (this.shouldTryAgainWithNewSocket) {
      this.shouldTryAgainWithNewSocket = false
      // Wait for 100 to 500 milliseconds before reconnecting
      const delay =
        Math.floor(Math.random() * SignalRSocket.JITTER_MAX_MILLISECONDS) + SignalRSocket.JITTER_MIN_MILLISECONDS
      setTimeout(() => this.startOrContinueStreaming(this.xhrConnectionUrl, true), delay)
    }
  }

  socketShouldRetry(socket: Socket, code: number): boolean {
    if (code === SignalRSocket.ABNORMAL_CLOSURE) {
      /**
       * AFD (Azure Front Door) abnormally closed the socket. This can happen after the websocket max time limit
       * has been reached which is configured to 10 minutes for Actions. An abnormal closure can also sometimes
       * randomly happen before the max time limit has been reached which stops incoming logs.
       *
       * Retrying with the existing socket is futile, but it is safe to discard the existing socket and create a new one.
       */
      this.shouldTryAgainWithNewSocket = true
      return false
    }
    return !this.isFatalStatusCode(code)
  }

  async startOrContinueStreaming(xhrConnectionUrl: string, retryingAbnormalClosure = false) {
    if (!this.xhrConnectionUrl) this.xhrConnectionUrl = xhrConnectionUrl

    if (this.isStreaming && !retryingAbnormalClosure) return
    this.isStreaming = true

    await this.tryStartNewStream(this.xhrConnectionUrl)
  }

  closeExistingSocket() {
    if (this.globalSocket) {
      this.globalSocket.close()
      this.globalSocket = null
    }
    return
  }

  onStreamFailure() {
    this.dispatchEvent(new CustomEvent('onStreamFailure'))
  }

  get reconnectWindow() {
    // Wait for 100 to 500 milliseconds before reconnecting
    const delay =
      Math.floor(Math.random() * SignalRSocket.JITTER_MAX_MILLISECONDS) + SignalRSocket.JITTER_MIN_MILLISECONDS
    return delay
  }

  // Private methods
  private async tryStartNewStream(xhrConnectionUrl: string) {
    try {
      const socketUrl = await this.retrieveSocketURL(xhrConnectionUrl)
      if (!socketUrl) return
      this.socketUrl = socketUrl

      this.globalSocket = new StableSocket(this.socketUrl, this, {timeout: 4000, attempts: 10})
      await this.globalSocket.open()
    } catch (e) {
      this.onStreamFailure()
      throw e
    }
  }

  private handshake() {
    const {searchParams} = new URL(this.socketUrl, window.location.origin)
    const tenantId = searchParams.get('tenantId')
    const runId = searchParams.get('runId')
    if (tenantId && runId) {
      const protocolPayload: HandshakeRequest = {
        protocol: 'json',
        version: 1,
      }
      this.sendPayload(protocolPayload)

      const targetPayload: Invocation = {
        arguments: [tenantId, +runId],
        target: this.target,
        type: MessageType.Invocation,
      }
      this.sendPayload(targetPayload)
    }
  }

  private retrieveSocketURL = async (xhrUrl: string): Promise<string | null> => {
    const authResult = await fetchJson<AuthenticatedURLResponse>(xhrUrl)
    const authenticatedUrl = authResult?.data['authenticated_url']
    if (!authenticatedUrl) return null
    const wsResult = await fetchJson<WsUrlLookupData>(authenticatedUrl)
    return wsResult?.logStreamWebSocketUrl ?? null
  }

  private isFatalStatusCode(code: number): boolean {
    return code === SignalRSocket.POLICY_VIOLATION
  }

  private sendPayload(payload: Message) {
    if (this.globalSocket) {
      this.globalSocket.send(JSON.stringify(payload) + SignalRSocket.RECORD_SEPARATOR)
    }
  }
}
